import type { PoolShippingSettleSummary, PoolShippingStatus } from '../../../../modules/api';

type Props = {
  status: PoolShippingStatus;
  label: string;
  settleSummary?: PoolShippingSettleSummary;
};

// When the warehouse couldn't ship every card, the derived summary takes over
// the chip (amber warning palette, shared with the per-card "Refunded" badge).
const SETTLE_SUMMARY_LABEL: Record<'refunded' | 'partially_shipped', string> = {
  refunded: 'Refunded',
  partially_shipped: 'Partially Shipped',
};

const CHIP_CLASS = 'inline-flex shrink-0 items-center rounded-[6px] px-2 py-[2px] text-[12px] font-medium leading-[16px]';

/** A shipment's status tag (partner Figma 2026-10-08): grey while it's being
 *  processed, green once shipped or delivered. */
export function ShippingStatusChip({ status, label, settleSummary }: Props) {
  if (settleSummary) {
    return (
      <span
        data-co-component="ShippingStatus"
        className={CHIP_CLASS}
        style={{
          background: 'var(--co-ShippingStatus-refundedBg, var(--co-ShippingItemStatus-refundedBg, rgba(255,176,32,0.20)))',
          color: 'var(--co-ShippingStatus-refundedFg, var(--co-ShippingItemStatus-refundedFg, #FFB020))',
        }}
      >
        {SETTLE_SUMMARY_LABEL[settleSummary]}
      </span>
    );
  }
  const { bg, fg } = statusColors(status);
  return (
    <span data-co-component="ShippingStatus" className={CHIP_CLASS} style={{ background: bg, color: fg }}>
      {label || status}
    </span>
  );
}

// Themed through the ShippingStatus knobs. Sleek used to paint this chip with
// the per-card badge knobs, so those stay in the chain for themes saved then.
function statusColors(status: PoolShippingStatus): { bg: string; fg: string } {
  switch (status) {
    case 'shipped':
    case 'delivered':
      return {
        bg: 'var(--co-ShippingStatus-shippedBg, var(--co-ShippingItemStatus-shippedBg, rgba(22,163,74,0.08)))',
        fg: 'var(--co-ShippingStatus-shippedFg, var(--co-ShippingItemStatus-shippedFg, #16A34A))',
      };
    case 'canceled':
      return {
        bg: 'var(--co-ShippingStatus-canceledBg, var(--co-ShippingItemStatus-canceledBg, rgba(255,255,255,0.06)))',
        fg: 'var(--co-ShippingStatus-canceledFg, var(--co-ShippingItemStatus-canceledFg, rgba(255,255,255,0.40)))',
      };
    case 'pending':
    default:
      return {
        bg: 'var(--co-ShippingStatus-pendingBg, var(--co-ShippingItemStatus-pendingBg, #25262C))',
        fg: 'var(--co-ShippingStatus-pendingFg, var(--co-ShippingItemStatus-pendingFg, #6D707C))',
      };
  }
}
